import { ConfirmButton } from "@/components/common/ConfirmButton";
import { pluginActionKey, pluginIdentity, type MarketplacePlugin } from "@/lib/marketplace";

interface PluginActionsProps {
	plugin: MarketplacePlugin;
	actionKey: string | null;
	actionError: string | null;
	actionNotice: string | null;
	onInstall: () => void;
	onUninstall: () => void;
	onSetEnabled: (enabled: boolean) => void;
	onUpdate: () => void;
}

/** プラグインの現在状態に応じた操作と、操作結果のインライン通知を表示する。 */
export function PluginActions({
	plugin,
	actionKey,
	actionError,
	actionNotice,
	onInstall,
	onUninstall,
	onSetEnabled,
	onUpdate,
}: PluginActionsProps) {
	const canOperate = plugin.key !== null && plugin.supported && plugin.installed !== null;
	const installKey = pluginActionKey(plugin, "install");
	const uninstallKey = pluginActionKey(plugin, "uninstall");
	const enablementAction = plugin.enabled === true ? "disable" : "enable";
	const enablementKey = pluginActionKey(plugin, enablementAction);
	const updateKey = pluginActionKey(plugin, "update");
	const disabledByOtherAction = (key: string) => actionKey !== null && actionKey !== key;

	return (
		<div
			style={{
				padding: 14,
				border: "var(--pane-header-border)",
				borderRadius: "var(--radius-3)",
				background: "var(--surface-2)",
			}}
		>
			<div
				style={{
					fontSize: "var(--text-caption)",
					fontWeight: 600,
					color: "var(--fg-2)",
					marginBottom: 10,
				}}
			>
				操作
			</div>
			{canOperate ? (
				<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
					{plugin.installed === false ? (
						<ConfirmButton
							label="インストール"
							loading={actionKey === installKey}
							disabled={disabledByOtherAction(installKey)}
							onClick={onInstall}
							data-testid={`plugin-install-${pluginIdentity(plugin)}`}
						/>
					) : (
						<>
							{plugin.enabled !== null && (
								<ConfirmButton
									label={plugin.enabled ? "無効にする" : "有効にする"}
									loading={actionKey === enablementKey}
									disabled={disabledByOtherAction(enablementKey)}
									variant="secondary"
									onClick={() => onSetEnabled(!plugin.enabled)}
									data-testid={`plugin-${enablementAction}-${pluginIdentity(plugin)}`}
								/>
							)}
							{plugin.updateAvailable === true && (
								<ConfirmButton
									label="更新"
									loading={actionKey === updateKey}
									disabled={disabledByOtherAction(updateKey)}
									onClick={onUpdate}
									data-testid={`plugin-update-${pluginIdentity(plugin)}`}
								/>
							)}
							<ConfirmButton
								label="アンインストール"
								loading={actionKey === uninstallKey}
								disabled={disabledByOtherAction(uninstallKey)}
								variant="destructive"
								onClick={onUninstall}
								data-testid={`plugin-uninstall-${pluginIdentity(plugin)}`}
							/>
						</>
					)}
				</div>
			) : (
				<p style={{ margin: 0, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
					{plugin.installed === null
						? "インストール状態を確認できないため操作できません。"
						: "このプラグインは Izanagi で操作できません。"}
				</p>
			)}
			{actionError && (
				<div
					style={{
						marginTop: 10,
						padding: 9,
						borderRadius: "var(--radius-3)",
						background: "var(--danger-subtle)",
						color: "var(--danger)",
						fontSize: "var(--text-caption)",
					}}
					data-testid="plugin-action-error"
				>
					{actionError}
				</div>
			)}
			{actionNotice && (
				<div
					style={{
						marginTop: 10,
						padding: 9,
						borderRadius: "var(--radius-3)",
						background: "var(--success-subtle)",
						color: "var(--success)",
						fontSize: "var(--text-caption)",
					}}
					data-testid="plugin-action-notice"
				>
					{actionNotice}
				</div>
			)}
		</div>
	);
}
